import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { TimeInput } from "@/components/ui/time-input";
import { TimezoneService } from "@/lib/timezone";
import { cn } from "@/lib/utils";
import { useSalonReviews } from "../hooks/use-salon-reviews";
import { useReviewUIStore } from "../store/use-review-ui-store";
import type { SalonReviewItem } from "../types";
import { StarRatingInput } from "./star-rating-input";

type Translator = (key: string, options?: Record<string, unknown>) => string;

const buildReviewSchema = (t: Translator) =>
  z.object({
    reviewerName: z.string().min(1, t("reviews.validation.reviewerNameRequired")).max(200),
    starRating: z.number().int().min(1, t("reviews.validation.ratingRequired")).max(5),
    comment: z.string().max(5000).optional(),
    reviewDate: z.custom<DateTime>(
      (val) => val instanceof DateTime && val.isValid,
      t("reviews.validation.dateRequired")
    ),
    reviewTimeText: z.string().regex(/^\d{2}:\d{2}$/, t("reviews.validation.timeRequired")),
  });

type ReviewFormValues = z.infer<ReturnType<typeof buildReviewSchema>>;

function buildReviewTime(date: DateTime, time: string): DateTime {
  const [hour, minute] = time.split(":").map(Number);
  return date.set({ hour, minute, second: 0, millisecond: 0 });
}

export function SalonReviewFormDialog({ review }: { review?: SalonReviewItem }) {
  const { t } = useTranslation("settings");
  const { isCreateDialogOpen, isEditDialogOpen, closeCreateDialog, closeEditDialog } =
    useReviewUIStore();
  const { createReview, updateReview, creating, updating } = useSalonReviews();

  const isOpen = isCreateDialogOpen || isEditDialogOpen;
  const isEditing = isEditDialogOpen && !!review;
  const isLoading = creating || updating;

  const now = DateTime.now().setZone(TimezoneService.getCurrentSalonTimezone());

  const schema = useMemo(() => buildReviewSchema(t as Translator), [t]);

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      reviewerName: "",
      starRating: 0,
      comment: "",
      reviewDate: now.startOf("day"),
      reviewTimeText: now.toFormat("HH:mm"),
    },
  });

  useEffect(() => {
    if (isEditing && review) {
      const existing = DateTime.fromISO(review.reviewTime).setZone(
        TimezoneService.getCurrentSalonTimezone()
      );
      form.reset({
        reviewerName: review.reviewerName,
        starRating: review.starRating,
        comment: review.comment ?? "",
        reviewDate: existing.startOf("day"),
        reviewTimeText: existing.toFormat("HH:mm"),
      });
    } else if (isCreateDialogOpen) {
      const fresh = DateTime.now().setZone(TimezoneService.getCurrentSalonTimezone());
      form.reset({
        reviewerName: "",
        starRating: 0,
        comment: "",
        reviewDate: fresh.startOf("day"),
        reviewTimeText: fresh.toFormat("HH:mm"),
      });
    }
  }, [isEditing, review, isCreateDialogOpen, form]);

  const handleClose = () => {
    if (isCreateDialogOpen) closeCreateDialog();
    else closeEditDialog();
  };

  const onSubmit = async (values: ReviewFormValues) => {
    const reviewTime = buildReviewTime(values.reviewDate, values.reviewTimeText);
    const isoTime = reviewTime.toUTC().toISO();
    if (!isoTime) return;

    try {
      if (isEditing && review) {
        await updateReview(review.id, {
          reviewerName: values.reviewerName,
          starRating: values.starRating,
          comment: values.comment?.trim() ? values.comment : null,
          reviewTime: isoTime,
        });
      } else {
        await createReview({
          reviewerName: values.reviewerName,
          starRating: values.starRating,
          comment: values.comment?.trim() ? values.comment : null,
          reviewTime: isoTime,
        });
      }
      handleClose();
    } catch {
      // handled by useAsyncAction
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? t("reviews.editReview") : t("reviews.newReview")}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reviewerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("reviews.form.reviewerName")}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t("reviews.form.reviewerNamePlaceholder")}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="reviewDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t("reviews.form.date")}</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                            disabled={isLoading}
                          >
                            {field.value ? (
                              field.value.toFormat("MMM dd, yyyy")
                            ) : (
                              <span>{t("reviews.form.pickDate")}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="!w-[350px] p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            field.value
                              ? new Date(field.value.year, field.value.month - 1, field.value.day)
                              : undefined
                          }
                          onSelect={(date) => {
                            if (!date) return;
                            field.onChange(
                              DateTime.fromObject(
                                {
                                  year: date.getFullYear(),
                                  month: date.getMonth() + 1,
                                  day: date.getDate(),
                                },
                                { zone: TimezoneService.getCurrentSalonTimezone() }
                              )
                            );
                          }}
                          disabled={isLoading}
                          initialFocus
                          className="w-full [--cell-size:2.5rem]"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="reviewTimeText"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>{t("reviews.form.time")}</FormLabel>
                    <FormControl>
                      <TimeInput
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="starRating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("reviews.form.starRating")}</FormLabel>
                  <FormControl>
                    <StarRatingInput
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("reviews.form.comment")}</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder={t("reviews.form.commentPlaceholder")}
                      disabled={isLoading}
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                {t("reviews.form.cancel")}
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Spinner className="w-4 h-4 mr-2" />}
                {isEditing ? t("reviews.form.update") : t("reviews.form.add")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
